import * as Sentry from "@sentry/vue";
import { type QueryClient } from "@tanstack/vue-query";
import { Amplify } from "aws-amplify";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

import type { AdminApp } from "@/misc/types";
import { currentUserQuery } from "@/users/api/queries";

import amplifyConfig from "../../amplifyAuth.config";

export const tryGetUserData = async (app: AdminApp) => {
  let token: string | undefined;
  try {
    const currentAuthSession = await fetchAuthSession();
    token = currentAuthSession?.tokens?.accessToken.toString();
  } catch {
    // `fetchAuthSession` throws if refreshing the token fails, so signout
    await signOut();

    return;
  }

  if (!token) return;

  try {
    const queryClient = app.config.globalProperties.$queryClient as QueryClient;

    return await queryClient.ensureQueryData(currentUserQuery);
  } catch {
    // there is a token, but we can't fetch the user for that token, so signout
    await signOut();

    return;
  }
};

Amplify.configure(amplifyConfig, { ssr: false });

Hub.listen("auth", ({ payload }) => {
  if (payload.event === "customOAuthState") {
    const state = payload.data; // this will be customState provided on signInWithRedirect function
    const objectifiedState = state ? JSON.parse(decodeURIComponent(state)) : {};

    const redirectTo = objectifiedState?.redirectTo
      ? String(objectifiedState.redirectTo)
      : "/";

    window.location.href = redirectTo;
  }
});

export default async (app: AdminApp) => {
  const user = await tryGetUserData(app);

  if (!user) return;

  Sentry.setUser({ email: `${user.email}` });
  app.$analytics?.identify(`${user.id}`, {
    username: user.email,
    email: user.email,
    identityUserId: user.id,
    permissions: user.permissions,
  });
};
